/**
 * Deep-link returnTo plumbing (Sprint 4a G4-2 finish).
 *
 * Auth0 supports an `appState.returnTo` field that survives the Auth0
 * round-trip via the `onRedirectCallback` prop on Auth0Provider. Without
 * it, a user who tries to deep-link into `/sentinel/flags` (or any
 * protected route) while signed out always lands back at `/dashboard`
 * after Auth0 — losing the original intent.
 *
 * This module exposes:
 *   * SESSION_RETURN_TO_KEY    — sessionStorage key for the captured path
 *   * captureReturnTo(path)    — write the path to sessionStorage
 *   * popReturnTo()            — read + clear (one-shot)
 *
 * Sprint 4a journey: a moderator gets a webhook email linking to
 * `/sentinel/flags/{flag_id}`; the email link sends them through Auth0;
 * after auth they land back on that exact URL, not on `/dashboard`.
 *
 * The sessionStorage layer handles the case where Auth0's appState is
 * lost (refresh-mid-flow, cross-tab) — we double-buffer through the
 * browser so a returnTo always survives.
 */

export const SESSION_RETURN_TO_KEY = 'curaition_auth_return_to';

/**
 * Paths that would cause a redirect loop if bounced back to. Note we
 * allow `/auth/accept-invite` (legitimate destination for org-member
 * provisioning) but block `/signin` and `/auth/callback` which exist
 * solely to handle the OAuth round-trip.
 */
function isSafeReturnTo(path: string): boolean {
  if (!path.startsWith('/')) return false; // no absolute URLs / external
  if (path.startsWith('//')) return false; // protocol-relative
  if (path.startsWith('/signin')) return false; // signin → signin loop
  if (path.startsWith('/auth/callback')) return false; // callback → callback loop
  return true;
}

export function captureReturnTo(path: string): void {
  if (!isSafeReturnTo(path)) return;
  try {
    sessionStorage.setItem(SESSION_RETURN_TO_KEY, path);
  } catch {
    // sessionStorage unavailable (private mode / SSR) — silent no-op.
  }
}

export function popReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(SESSION_RETURN_TO_KEY);
    if (value !== null) {
      sessionStorage.removeItem(SESSION_RETURN_TO_KEY);
    }
    return value && isSafeReturnTo(value) ? value : null;
  } catch {
    return null;
  }
}
